// Componente Angular para la gestión de bajas de los empleados asociados con un usuario.
// Se importa en bajas.module.ts

import { Component, OnInit, OnDestroy } from "@angular/core";
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from "@angular/router";
import { EmployeesListService } from "../employees-list.service";
import { BajasService } from "../../../services/employees/bajas.service";
import { Observable, Subscription } from "rxjs";
import { Bajas, BajasDTO } from "../../../models/personal/bajas.models";
import { FicherosEmpleadosService } from "../../../services/employees/ficheros-empleados.service";
import { EmployeesListModel } from "../employees-list.model";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { EMPLOYEES_ROUTES } from "../const";
import { consts } from "../../../consts";
import { Utils } from "../../../shared/utils";
import { DummyComponent } from "../../../shared/dummy.component";
import { BajasFormComponent } from "./bajas-form.component";
import { BajasConfirmDeleteComponent, ConfirmDeleteData } from "./bajas-confirm-delete.component";

@Component({
    selector: "app-bajas",
    templateUrl: "./bajas.component.html",
    styleUrls: ["./bajas.component.css"],
    standalone: false
})
export class BajasComponent implements OnInit, OnDestroy {
    // #region Propiedades del componente
    empleados: EmployeesListModel[] = [];
    displayedColumnsByEmployee: string[] = [
        "employee",
        "cadenaInicio",
        "cadenaFin",
        "motivo",
        "cadenaFechaSolicitud",
        "acciones",
    ];
    displayedColumnsActiveBajas: string[] = [
        "employee",
        "cadenaInicio",
        "cadenaFin",
        "motivo",
        "cadenaFechaSolicitud",
        "acciones",
    ];
    row: BajasDTO = null;
    routes: object[] = [];

    formBusqueda: UntypedFormGroup = null;

    // Indicadores de estado
    isDownloading: boolean = false;

    // Suscripciones
    private downloadSubscription: Subscription;
    // #endregion

    constructor(
        private router: Router,
        public dialog: MatDialog,
        public dummyComponent: DummyComponent,
        public EmployeesListService: EmployeesListService,
        public BajasService: BajasService,
        protected snackBar: MatSnackBar,
        private ficherosEmpleadosService: FicherosEmpleadosService
    ) {
        this.routes = EMPLOYEES_ROUTES;

        // Obtención de los empleados ...
        this.EmployeesListService.getList(true).then((data) => {
            this.empleados = data;
            this.inicializarFormulario();
        });
    }

    cancelar(): void {
        if (this.formBusqueda != null) {
            this.formBusqueda.reset();
        }

        this.BajasService.bajas$.next([]);
    }

    create(): void {
        if (!Utils.IsTierFour()) {
            this.snackBar.open(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE, null, {
                duration: 3000,
            });
            return;
        }

        const dialogRef = this.dialog.open(BajasFormComponent, {
            width: "90%",
            maxWidth: "900px",
            maxHeight: "90vh",
            data: { empleados: this.empleados, bajas: null },
        });
        dialogRef.afterClosed().subscribe((result) => {
            this.dummyComponent.redirectTo(this.router.url);
        });
    }

    createBajaForEmployee(): void {
        if (!Utils.IsTierFour()) {
            this.snackBar.open(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE, null, {
                duration: 3000,
            });
            return;
        }

        const empleadoSeleccionado = this.empleados.find(
            (e) => e.uid === this.formBusqueda.value.employees_Id
        );

        const dialogRef = this.dialog.open(BajasFormComponent, {
            width: "90%",
            maxWidth: "900px",
            maxHeight: "90vh",
            data: {
                empleados: this.empleados,
                bajas: null,
                empleadoSeleccionado: empleadoSeleccionado,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            this.dummyComponent.redirectTo(this.router.url);
        });
    }

    descargarDocumento(fileGuid: string): void {
        // Evitar múltiples descargas simultáneas
        if (this.isDownloading) {
            return;
        }

        // Crear un objeto fichero temporal para usar el servicio de ficheros
        const ficheroTemp = {
            fichero: {
                uid: fileGuid,
                type: 'pdf' // Por defecto, se puede ajustar según el tipo real del archivo
            }
        } as any;

        this.ficherosEmpleadosService.viewDocument(ficheroTemp);
    }

    editar(): void {
        if (!Utils.IsTierFour()) {
            this.snackBar.open(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE, null, {
                duration: 3000,
            });
            return;
        }

        const dialogRef = this.dialog.open(BajasFormComponent, {
            width: "90%",
            maxWidth: "900px",
            maxHeight: "90vh",
            data: { bajas: this.row.baja, empleados: this.empleados },
        });
        dialogRef.afterClosed().subscribe((result) => {
            this.dummyComponent.redirectTo(this.router.url);
        });
    }

    eliminar(): void {
        if (!Utils.IsTierFour()) {
            this.snackBar.open(consts.UPDATE_SUBSCRIPTION_TIER_MESSAGE, null, {
                duration: 3000,
            });
            return;
        }

        // Obtener el nombre del empleado para mostrar en el diálogo
        const empleadoNombre = this.nombreEmpleado(this.row.baja.employees_Id);

        // Preparar los datos para el diálogo de confirmación
        const confirmData: ConfirmDeleteData = {
            empleadoNombre: empleadoNombre,
            fechaInicio: this.formatearFecha(this.row.baja.inicio),
            motivo: this.row.baja.motivo || 'No especificado'
        };

        // Mostrar diálogo de confirmación
        const dialogRef = this.dialog.open(BajasConfirmDeleteComponent, {
            width: '500px',
            data: confirmData,
            disableClose: true
        });

        // Procesar la respuesta del diálogo
        dialogRef.afterClosed().subscribe((confirmado: boolean) => {
            if (confirmado) {
                // Usuario confirmó la eliminación
                this.ejecutarEliminacion();
            }
        });
    }

    /**
     * Ejecuta la eliminación de la baja después de la confirmación
     */
    private ejecutarEliminacion(): void {
        this.BajasService.delete(this.row.baja.id)
            .then((resultado) => {
                if (resultado) {
                    this.snackBar.open('Baja eliminada correctamente', null, {
                        duration: 3000,
                    });
                    this.dummyComponent.redirectTo(this.router.url);
                }
            })
            .catch((error) => {
                console.error("Error al eliminar la baja:", error);
                this.snackBar.open('Error al eliminar la baja', null, {
                    duration: 3000,
                });
            });
    }

    ngOnInit() {
        // Inicializar el observable de bajas con un array vacío para evitar errores
        this.BajasService.bajas$.next([]);

        // Cargar las bajas activas del sistema
        this.cargarBajasActivas();

        // Suscribirse al observable de descarga de archivo
        this.downloadSubscription = this.ficherosEmpleadosService.downloadingFile$.subscribe(
            (isDownloading: boolean) => {
                this.isDownloading = isDownloading;
            }
        );
    }

    ngOnDestroy(): void {
        // Limpiar suscripciones para evitar memory leaks
        if (this.downloadSubscription) {
            this.downloadSubscription.unsubscribe();
        }
    }

    nombreEmpleado(uid: number): string {
        const empleado = this.empleados.find((e) => e.uid == uid);
        return empleado ? empleado.name : "";
    }

    obtenerBajasPorEmpleado(): void {
        if (this.formBusqueda.valid) {
            console.log(
                "Buscando bajas para empleado:",
                this.formBusqueda.value.employees_Id
            );

            this.BajasService.getAllBajasByEmployee(
                this.formBusqueda.value.employees_Id
            )
                .then((resultado) => {
                })
                .catch((error) => {
                    console.error(
                        "Error al obtener bajas por empleado:",
                        error
                    );
                    this.BajasService.bajas$.next([]);
                    this.snackBar.open(
                        "Error al obtener las bajas del empleado",
                        null,
                        {
                            duration: 3000,
                        }
                    );
                });
        }
    }

    private formatearFecha(fecha: string): string {
        if (!fecha) return "";
        try {
            const date = new Date(fecha);
            return date.toLocaleDateString("es-ES");
        } catch (error) {
            return fecha;
        }
    }

    setRow(row: BajasDTO): void {
        // Asegurar que row tenga documentos inicializados
        if (row && !row.documentos) {
            row.documentos = [];
        }
        this.row = row;
    }

    private inicializarFormulario() {
        this.formBusqueda = new UntypedFormGroup({
            employees_Id: new UntypedFormControl(undefined, [Validators.required]),
        });
    }

    /**
     * Carga las bajas activas del sistema
     */
    private cargarBajasActivas(): void {
        this.BajasService.getActiveBajasByUser()
            .then((resultado) => {
                console.log("Bajas activas cargadas:", resultado);
            })
            .catch((error) => {
                console.error("Error al cargar bajas activas:", error);
                this.snackBar.open(
                    "Error al cargar las bajas activas",
                    null,
                    {
                        duration: 3000,
                    }
                );
            });
    }

    /**
     * Verifica si una baja tiene documentos válidos
     * @param baja La baja a verificar
     * @returns true si la baja tiene documentos válidos
     */
    public tieneDocumentos(baja: BajasDTO): boolean {
        return (
            baja &&
            baja.documentos &&
            Array.isArray(baja.documentos) &&
            baja.documentos.length > 0
        );
    }
  public trackByIndex(i: number) { return i; }

}
